import DownloadIcon from '@mui/icons-material/Download';
import { Button, Stack } from '@mui/material';
import { map, sortBy } from 'lodash';
import { useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { AudioClip, getAudioClipUrl } from '@/api/audioClips';
import { downloadUrls } from '@/utils/downloadHelpers';

const DOWNLOAD_INTERVAL_MS = 1000;

const SAVE_BUTTON_SX = { bgcolor: 'background.paper', textWrap: 'nowrap' } as const;

export type AudioClipSaveButtonsProps = {
    spotterId: string;
    /** The `succeeded` clips that the clip lane shows. */
    savableClips: AudioClip[];
    /** The clip of `savableClips` under the selection line, or `undefined` when there is none. */
    selectedClip: AudioClip | undefined;
};

export const AudioClipSaveButtons = ({
    spotterId,
    savableClips,
    selectedClip,
}: AudioClipSaveButtonsProps): JSX.Element => {
    const mixpanel = useMixpanel();
    const [isSavingAll, setIsSavingAll] = useState(false);
    const downloadUrl = (clip: AudioClip) => getAudioClipUrl(spotterId, clip.id, { download: true });

    const handleSaveAll = async () => {
        const clips = sortBy(savableClips, (clip) => new Date(clip.start_time).getTime());

        mixpanel?.track('Save all clips', {
            spotterId,
            bristlemouthNodeId: clips[0]?.bristlemouth_node_id,
            audioExfilIds: map(clips, 'id'),
        });
        setIsSavingAll(true);
        try {
            await downloadUrls(map(clips, downloadUrl), { intervalMs: DOWNLOAD_INTERVAL_MS });
        } finally {
            setIsSavingAll(false);
        }
    };

    const handleSaveSelected = async () => {
        if (!selectedClip) {
            return;
        }

        mixpanel?.track('Save selected clip', {
            spotterId,
            bristlemouthNodeId: selectedClip.bristlemouth_node_id,
            audioExfilId: selectedClip.id,
        });
        await downloadUrls([downloadUrl(selectedClip)], { intervalMs: DOWNLOAD_INTERVAL_MS });
    };

    return (
        <Stack sx={{ m: 1, gap: 1 }}>
            <Button
                variant="outlined"
                fullWidth
                startIcon={<DownloadIcon />}
                loading={isSavingAll}
                loadingPosition="start"
                disabled={!savableClips.length}
                onClick={handleSaveAll}
                sx={SAVE_BUTTON_SX}
            >
                Save all snippets
            </Button>
            <Button
                variant="outlined"
                fullWidth
                startIcon={<DownloadIcon />}
                disabled={!selectedClip}
                onClick={handleSaveSelected}
                sx={SAVE_BUTTON_SX}
            >
                Save selected snippet
            </Button>
        </Stack>
    );
};
